import type { NavigateFunction } from 'react-router-dom';
import { ROUTES, TOKEN_STORAGE_KEYS } from './constants';
import { clearLmsLaunchSession } from './lmsSession';

/** Thrown after auth-expired handling so callers skip generic error UI / raw JSON. */
export class AuthSessionExpiredHandledError extends Error {
  constructor() {
    super('AuthSessionExpiredHandled');
    this.name = 'AuthSessionExpiredHandledError';
    Object.setPrototypeOf(this, AuthSessionExpiredHandledError.prototype);
  }
}

export function isAuthSessionExpiredHandledError(e: unknown): boolean {
  return e instanceof AuthSessionExpiredHandledError;
}

const AUTH_FAILURE_CODES = new Set([
  'AUTH_INVALID_TOKEN',
  'AUTH_MISSING_TOKEN',
  'UNAUTHORIZED',
  'TOKEN_EXPIRED',
]);

let logoutRedirectStarted = false;

const LMS_BOOTSTRAP_PREFIXES = [
  ROUTES.LMS_LAUNCH,
  ROUTES.AUTHENTICATE,
  ROUTES.EMBED_BANYANPRO,
] as const;

/** True while the app is exchanging an LMS assertion on a public bootstrap route. */
export function isLmsBootstrapPath(pathname: string): boolean {
  return LMS_BOOTSTRAP_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function isLogoutRedirectInProgress(): boolean {
  return logoutRedirectStarted;
}

/** @internal Vitest only — resets module logout guard between tests. */
export function __resetAuthSessionLogoutStateForTests(): void {
  logoutRedirectStarted = false;
}

export function clearAuthSessionStorage(): void {
  try {
    localStorage.removeItem('X_ROLE');
    localStorage.removeItem('X_TENANT_ID');
    localStorage.removeItem('X_USER_ID');
    localStorage.removeItem('tkm_selected_role');
    localStorage.removeItem('tamil_editor_content');
    localStorage.removeItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN);
    clearLmsLaunchSession();
  } catch {
    // ignore localStorage errors
  }
}

function normalizeBodyText(body: unknown): string {
  if (body == null) return '';
  if (typeof body === 'string') return body;
  try {
    return JSON.stringify(body);
  } catch {
    return String(body);
  }
}

function readErrorFieldsFromBody(body: unknown): { code?: string; message?: string } {
  const text = normalizeBodyText(body).trim();
  if (!text) return {};

  let obj: unknown = body;
  if (typeof body === 'string') {
    try {
      obj = JSON.parse(body) as unknown;
    } catch {
      return { message: body };
    }
  }

  if (!obj || typeof obj !== 'object') return { message: text };

  const o = obj as Record<string, unknown>;
  const directCode = typeof o.error_code === 'string' ? o.error_code : undefined;
  const directMessage =
    typeof o.message === 'string'
      ? o.message
      : typeof o.detail === 'string'
        ? o.detail
        : undefined;

  const err = o.error;
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    const nestedCode =
      typeof e.error_code === 'string'
        ? e.error_code
        : typeof e.code === 'string'
          ? e.code
          : undefined;
    const nestedMessage =
      typeof e.message === 'string'
        ? e.message
        : typeof e.detail === 'string'
          ? e.detail
          : undefined;
    return {
      code: nestedCode ?? directCode ?? (typeof o.code === 'string' ? o.code : undefined),
      message: nestedMessage ?? directMessage,
    };
  }

  return {
    code: directCode ?? (typeof o.code === 'string' ? o.code : undefined),
    message: directMessage,
  };
}

function messageIndicatesAuthFailure(message: string | undefined): boolean {
  if (!message) return false;
  const m = message.toLowerCase();
  return (
    m.includes('missing bearer token') ||
    m.includes('invalid token') ||
    m.includes('invalid bearer token') ||
    m.includes('expired access token') ||
    m.includes('invalid/expired access token')
  );
}

/**
 * True when an API response indicates invalid/expired/missing auth.
 */
export function isUnauthorizedApiResponse(status: number, body?: unknown): boolean {
  if (status === 401) return true;

  const { code, message } = readErrorFieldsFromBody(body);
  if (code && AUTH_FAILURE_CODES.has(code)) {
    return status >= 400 && status < 500;
  }
  if (messageIndicatesAuthFailure(message)) {
    return status >= 400 && status < 500;
  }

  const text = normalizeBodyText(body).toLowerCase();
  if (text.includes('auth_invalid_token') || text.includes('missing bearer token')) {
    return status >= 400 && status < 500;
  }

  return false;
}

/**
 * Clears session storage and redirects to login (unless already on login).
 * Safe to call multiple times; only one redirect is started.
 */
export function forceLogoutAndRedirect(navigate?: NavigateFunction): void {
  clearAuthSessionStorage();

  if (typeof window === 'undefined') {
    return;
  }

  const path = window.location.pathname;
  if (path === ROUTES.LOGIN || path.startsWith(`${ROUTES.LOGIN}/`)) {
    return;
  }

  if (isLmsBootstrapPath(path)) {
    return;
  }

  if (logoutRedirectStarted) return;
  logoutRedirectStarted = true;

  if (navigate) {
    navigate(ROUTES.LOGIN, { replace: true });
    return;
  }

  window.location.replace(ROUTES.LOGIN);
}

/**
 * For raw `fetch` callers: if unauthorized, logout + redirect and return true.
 */
export function handleUnauthorizedResponseIfPresent(options: {
  status: number;
  bodyText?: string;
  body?: unknown;
  navigate?: NavigateFunction;
}): boolean {
  if (logoutRedirectStarted) return true;

  const body =
    options.body ??
    (options.bodyText != null && options.bodyText !== '' ? options.bodyText : undefined);

  if (!isUnauthorizedApiResponse(options.status, body)) return false;

  forceLogoutAndRedirect(options.navigate);
  return true;
}

/**
 * For axios interceptors: logout + redirect when unauthorized; rejects with handled error.
 */
export function rejectWithAuthSessionExpired<T = never>(): Promise<T> {
  if (!logoutRedirectStarted) {
    forceLogoutAndRedirect();
  }
  return Promise.reject(new AuthSessionExpiredHandledError());
}
